import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { AdvancedDateRangePicker } from '@/components/ui/AdvancedDateRangePicker'
import { useAppSelector } from '@/app/hooks'
import { selectUser, selectIsAuthenticated } from '@/features/auth/authSlice'
import type { InviteeFilters } from '../types/invitee'
import { INVITE_STATUS_OPTIONS } from '../utils/inviteeConstants'
import { getProperties, getOrganizations, getCompanies } from '../services/inviteeService'
import { formatDateForAPI } from '../utils/inviteeFormatters'
import { Filter, X } from 'lucide-react'
import '../styles/inviteeFilters.css'

interface InviteeFiltersProps {
  filters: InviteeFilters
  onFiltersChange: (
    filters: Partial<InviteeFilters>,
    meta?: { org_name?: string; company_name?: string; property_name?: string }
  ) => void
  onReset: () => void
  isOpen: boolean
  onClose: () => void
}

export function InviteeFilters({ filters, onFiltersChange, onReset, isOpen, onClose }: InviteeFiltersProps) {
  useBodyScrollLock(isOpen)
  const user = useAppSelector(selectUser)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  
  // -- Data Loading State --
  const [organizations, setOrganizations] = useState<SearchableSelectOption[]>([])
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])
  const [properties, setProperties] = useState<SearchableSelectOption[]>([])
  const [loadingOrgs, setLoadingOrgs] = useState(false)
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingProperties, setLoadingProperties] = useState(false)
  const propertiesRequestIdRef = useRef(0)
  
  // -- Role Locking --
  const lockedOrgId = useMemo(() => user?.current_role?.org_id ? String(user.current_role.org_id) : undefined, [user])
  const lockedCompanyId = useMemo(() => user?.current_role?.company_id ? String(user.current_role.company_id) : undefined, [user])
  const lockedPropertyId = useMemo(() => user?.current_role?.property_id ? String(user.current_role.property_id) : undefined, [user])

  // -- Data Fetchers (Simplied reuse of existing logic) --
  const loadOrganizations = useCallback(async (search: string) => {
    // Don't make API calls if not authenticated yet
    if (!isAuthenticated) {
      return
    }
    setLoadingOrgs(true)
    try {
        const data = await getOrganizations(search)
        setOrganizations(data.map(org => ({ value: String(org.org_id), label: org.org_name || String(org.org_id) })))
    } catch (e) { console.error(e) } finally { setLoadingOrgs(false) }
  }, [isAuthenticated])
  const loadCompanies = useCallback(async (search: string, orgId?: string) => {
    // Don't make API calls if not authenticated yet
    if (!isAuthenticated) {
      return
    }
     setLoadingCompanies(true)
     try {
        const data = await getCompanies(search, orgId)
        setCompanies(data.map(c => ({ value: String(c.company_id), label: c.company_name || String(c.company_id) })))
     } catch (e) { console.error(e) } finally { setLoadingCompanies(false) }
  }, [isAuthenticated])
  const loadProperties = useCallback(async (search: string, companyId?: string, orgId?: string) => {
    // Don't make API calls if not authenticated yet
    if (!isAuthenticated) {
      return
    }
    const requestId = propertiesRequestIdRef.current + 1
    propertiesRequestIdRef.current = requestId
    setLoadingProperties(true)
    try {
        const data = await getProperties(search, companyId, orgId)
        if (requestId !== propertiesRequestIdRef.current) return
        setProperties(data.map(p => ({ value: String(p.property_id), label: p.property_name || String(p.property_id) })))
    } catch (e) { console.error(e) } finally {
      if (requestId === propertiesRequestIdRef.current) {
        setLoadingProperties(false)
      }
    }
  }, [isAuthenticated])

  /**
   * Auto-apply locked filter values from user's role to the actual filter state.
   * Mirrors Angular getAccessiblePermission() which patches org_id/company_id/property_id
   * into filterForm so they are included in the API request.
   * Uses value comparison (not a ref) so locked values are re-applied after resetFilter().
   */
  useEffect(() => {
    if (!isAuthenticated) return
    const lockedValues: Partial<InviteeFilters> = {}
    if (lockedOrgId && filters.org_id !== lockedOrgId) lockedValues.org_id = lockedOrgId
    if (lockedCompanyId && filters.company_id !== lockedCompanyId) lockedValues.company_id = lockedCompanyId
    if (lockedPropertyId && filters.property_id !== lockedPropertyId) lockedValues.property_id = lockedPropertyId

    if (Object.keys(lockedValues).length > 0) {
      onFiltersChange(lockedValues)
    }
  }, [isAuthenticated, lockedOrgId, lockedCompanyId, lockedPropertyId, filters.org_id, filters.company_id, filters.property_id, onFiltersChange])

  // Initial Load - wait for authentication
  useEffect(() => {
    if (isAuthenticated) {
      if (!lockedOrgId && organizations.length === 0) loadOrganizations('')
    }
  }, [
    isAuthenticated,
    lockedOrgId,
    organizations.length,
    loadOrganizations,
  ])

  useEffect(() => {
    if (!isAuthenticated || lockedCompanyId) return
    setCompanies([])
    loadCompanies('', filters.org_id)
  }, [isAuthenticated, lockedCompanyId, filters.org_id, loadCompanies])

  useEffect(() => {
    if (!isAuthenticated || lockedPropertyId) return
    if ((!filters.company_id && !lockedCompanyId) && (!filters.org_id && !lockedOrgId)) {
      propertiesRequestIdRef.current += 1
      setProperties([])
      setLoadingProperties(false)
      return
    }

    setProperties([])
    loadProperties('', filters.company_id || lockedCompanyId, filters.org_id || lockedOrgId)
  }, [
    isAuthenticated,
    lockedPropertyId,
    lockedCompanyId,
    lockedOrgId,
    filters.company_id,
    filters.org_id,
    loadProperties,
  ])
  
  // -- Handlers --
  const handleDateChange = (startDate: Date | null, endDate: Date | null) => {
    onFiltersChange({
      fromDt: startDate ? formatDateForAPI(startDate) : undefined,
      toDt: endDate ? formatDateForAPI(endDate) : undefined
    })
  }

  // Handle drawer close logic
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  if (typeof document === 'undefined') return null

  return createPortal(
    <>
      {/* Backdrop — portaled so drawer is full-viewport (not under dashboard pt-14). */}
      <div
        data-testid="invitee-list-filters-drawer-backdrop"
        className={`fixed inset-0 z-[2147483646] h-[100dvh] bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div
        data-testid="invitee-list-filters-drawer"
        className={`touch-scroll fixed inset-y-0 right-0 z-[2147483647] flex h-[100dvh] max-h-[100dvh] w-full transform flex-col overflow-y-auto bg-white shadow-2xl transition-transform duration-300 ease-in-out sm:max-w-sm ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        
        {/* Drawer Header */}
        <div
          data-testid="invitee-list-filters-drawer-header"
          className="flex items-center justify-between px-6 py-4 border-b border-slate-100"
        >
            <div className="flex items-center gap-2 text-slate-800">
                <Filter className="h-5 w-5 text-[rgb(21_151_255)]" />
                <h2
                  data-testid="invitee-list-filters-drawer-title"
                  className="text-lg font-bold"
                >
                  Filters
                </h2>
            </div>
            <button
                type="button"
                data-testid="invitee-list-filters-drawer-close-button"
                onClick={onClose}
                className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-all"
            >
                <X className="h-5 w-5" />
            </button>
        </div>

        {/* Drawer Content (Scrollable) */}
        <div
          data-testid="invitee-list-filters-drawer-content"
          className="flex-1 overflow-y-auto p-6 space-y-6"
        >
            
            {/* -- 1. Status Chip -- */}
            <div
              data-testid="invitee-list-filters-invite-status-section"
              className="space-y-2"
            >
               <label
                 data-testid="invitee-list-filters-invite-status-label"
                 className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
               >
                 INVITES
               </label>
               <div className="relative pill-filter-wrapper-vertical">
                   <SearchableSelect
                      triggerDataTestId="invitee-list-filters-invite-status-select-trigger"
                      multiple
                      placeholder="Select Invites (multi-select)"
                      options={[...INVITE_STATUS_OPTIONS]}
                      value={filters.inviteStatus ?? []}
                      onChange={(val) => {
                        const next = Array.isArray(val) ? val : val ? [val] : []
                        onFiltersChange({
                          inviteStatus:
                            next.length > 0
                              ? (next as InviteeFilters['inviteStatus'])
                              : undefined,
                        })
                      }}
                   />
               </div>
            </div>

            {/* -- 2. Organization Chip -- */}
            <div
              data-testid="invitee-list-filters-organization-section"
              className="space-y-2"
            >
                <label
                  data-testid="invitee-list-filters-organization-label"
                  className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
                >
                  Organization
                </label>
                <div className="relative pill-filter-wrapper-vertical">
                    <SearchableSelect
                        triggerDataTestId="invitee-list-filters-organization-select-trigger"
                        placeholder="Select Organization"
                        options={lockedOrgId
                          ? [{ value: lockedOrgId, label: user?.current_role?.org_name || lockedOrgId }]
                          : organizations}
                        value={lockedOrgId || filters.org_id || ''}
                        onChange={(val) => {
                          if (lockedOrgId) return;
                          const orgId = Array.isArray(val) ? val[0] : val
                          onFiltersChange({ org_id: orgId || undefined, company_id: undefined, property_id: undefined })
                          setCompanies([])
                          setProperties([])
                          if (orgId) {
                            loadCompanies('', orgId)
                            loadProperties('', undefined, orgId)
                          }
                        }}
                        onSearch={lockedOrgId ? undefined : loadOrganizations}
                        loading={loadingOrgs}
                        disabled={!!lockedOrgId}
                    />
                </div>
            </div>

            {/* -- 3. Company Chip -- */}
            <div
              data-testid="invitee-list-filters-company-section"
              className="space-y-2"
            >
                <label
                  data-testid="invitee-list-filters-company-label"
                  className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
                >
                  Company
                </label>
                <div className="relative pill-filter-wrapper-vertical">
                    <SearchableSelect
                        triggerDataTestId="invitee-list-filters-company-select-trigger"
                        placeholder="Select Company"
                        options={lockedCompanyId
                          ? [{ value: lockedCompanyId, label: user?.current_role?.company_name || lockedCompanyId }]
                          : companies}
                        value={lockedCompanyId || filters.company_id || ''}
                        onChange={(val) => {
                          if (lockedCompanyId) return;
                          const companyId = Array.isArray(val) ? val[0] : val
                          onFiltersChange({ company_id: companyId || undefined, property_id: undefined })
                          setProperties([])
                          if (companyId || filters.org_id || lockedOrgId) {
                            loadProperties('', companyId || undefined, filters.org_id || lockedOrgId)
                          }
                        }}
                        onSearch={lockedCompanyId ? undefined : (s) => loadCompanies(s, filters.org_id)}
                        immediateSearchOnPaste
                        loading={loadingCompanies}
                        disabled={!!lockedCompanyId}
                    />
                </div>
            </div>

            {/* -- 4. Property Chip -- */}
            <div
              data-testid="invitee-list-filters-property-section"
              className="space-y-2"
            >
                <label
                  data-testid="invitee-list-filters-property-label"
                  className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
                >
                  Property
                </label>
                <div className="relative pill-filter-wrapper-vertical">
                    <SearchableSelect
                        triggerDataTestId="invitee-list-filters-property-select-trigger"
                        placeholder="Select Property"
                        options={lockedPropertyId
                          ? [{ value: lockedPropertyId, label: user?.current_role?.property_name || lockedPropertyId }]
                          : properties}
                        value={lockedPropertyId || filters.property_id || ''}
                        onChange={(val) => {
                          if (lockedPropertyId) return;
                          const propertyId = Array.isArray(val) ? val[0] : val
                          onFiltersChange({ property_id: propertyId || undefined })
                        }}
                        onSearch={lockedPropertyId ? undefined : (s) => loadProperties(s, filters.company_id, filters.org_id)}
                        immediateSearchOnPaste
                        disabled={!!lockedPropertyId || ((!filters.company_id && !lockedCompanyId) && (!filters.org_id && !lockedOrgId))}
                        loading={loadingProperties}
                    />
                </div>
            </div>

            {/* -- 5. Date Range Chip -- */}
            <div
              data-testid="invitee-list-filters-date-range-section"
              className="space-y-2"
            >
                <label
                  data-testid="invitee-list-filters-date-range-label"
                  className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
                >
                  Date Range
                </label>
                <div
                  data-testid="invitee-list-filters-date-range-picker-wrap"
                  className="relative pill-filter-wrapper-vertical"
                >
                     <AdvancedDateRangePicker
                        startDate={filters.fromDt ? new Date(filters.fromDt) : null}
                        endDate={filters.toDt ? new Date(filters.toDt) : null}
                        onChange={handleDateChange}
                        placeholder="Select Date Range"
                        presetsOnTop
                     />
                </div>
            </div>
        </div>

        {/* Drawer Footer */}
        <div
          data-testid="invitee-list-filters-drawer-footer"
          className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-wrap items-center justify-between gap-4"
        >
             <button
                type="button"
                data-testid="invitee-list-filters-reset-all-button"
                onClick={onReset}
                className="filter-drawer-reset-all"
            >
                Reset All
            </button>
             <button
                type="button"
                data-testid="invitee-list-filters-done-button"
                onClick={onClose}
                className="flex-1 bg-[rgb(21_151_255)] hover:bg-[#0d7acc] text-white font-semibold py-2.5 rounded-lg shadow-md shadow-[rgba(21,151,255,0.22)] transition-all text-sm"
            >
                Done
            </button>
        </div>
      </div>
    </>,
    document.body,
  )
}
